import React, { useEffect, useState } from 'react';
import { Spin, Tooltip } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';

import AgGrid from "../../../../../components/ag-grid-table/index";
import PlacementCard from '../../../../../components/uiComponent/placement-card';
import Button from "../../../../../components/uiComponent/button/index";

import { ConfirmPlacementOption, CancelInboundPlan, GetPlacementOptions, GetShipmentItems, updateState } from '../../../../../slices/shipment-plan';
import { GetWorkOrderDetails } from '../../../../../slices/work-orders';

import { PlacementOptionWrapper } from './style';

import { STORAGE_IMAGE_URL } from '../../../../../constants/storage.js';

const ProductImg = `${STORAGE_IMAGE_URL}/newImages/nestle.svg`;

const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true,
};

const PlacementOption = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [inboundPlanDataSet, setInboundPlanDataSet] = useState({ placementOptions: [], shipments: [], items: [] });
  const [selectedOptions, setSelectedOptions] = useState({ placementIndex: 0, shipmentIndex: 0 });

  const {
    workOrderDetails
  } = useSelector((state) => state.workOrders || {});
  const { currentUser } = useSelector((state) => state.auth || {});

  const {
    inboundPlans = [],
    confirmingPlacementOption,
    cancelingInboundPlan,
    fetchingPlacementOptions,
    fetchingShipmentItems,
  } = useSelector((state) => state.shipmentPlan || {});

  useEffect(() => {

    dispatch(GetWorkOrderDetails(workOrderId)).then(({ payload }) => {

      // if work order is not in draft state, redirect to confirm placement or work order page
      if (payload?.data?.status !== 'Draft') {
        if(currentUser?.role?.id === 5){
          navigate('/work-orders', {replace: true});
        }
        else{
          navigate(`/confirm-placement/${workOrderId}`);
        }
        return;
      }
      // clearing placement options states
      setInboundPlanDataSet({ placementOptions: [], shipments: [], items: [] });
      setSelectedOptions({ placementIndex: 0, shipmentIndex: 0 });
      dispatch(GetPlacementOptions(workOrderId))
        .then(({ payload }) => {
          const { placementOptions } = payload || {};

          const mappedPlacementOptions = placementOptions?.map((placementOption, index) => {
            const { discounts, expiration, fees, placementOptionId } = placementOption || {};

            return {
              id: placementOptionId,
              placementOption: `Placement Option ${index + 1}`,
              shipments: 0,
              discount: discounts?.reduce((total, fee) => total + (fee?.value?.amount || 0), 0) || 0,
              expiry: expiration ? dayjs(expiration).format("M/D/YY") : '',
              placementFee: fees?.[0]?.value?.amount || 0,
            };
          }) || [];

          setInboundPlanDataSet({
            placementOptions: mappedPlacementOptions
          });

          if (!isEmpty(placementOptions)) dispatch(GetShipmentItems({ placementOptions, workOrderId }));
        });
    });

    return () => {
      setInboundPlanDataSet({ placementOptions: [], shipments: [], items: [] });
      setSelectedOptions({ placementIndex: 0, shipmentIndex: 0 });
      dispatch(updateState({ key: 'inboundPlans', value: [] }));
      dispatch(updateState({ key: 'placementOptions', value: [] }));
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(inboundPlans)) {
      const placementOptions = inboundPlans?.map((plan, index) => {
        const { placementOption, shipmentDetails } = plan || {};
        const { discounts, expiration, fees, placementOptionId } = placementOption || {};

        return {
          id: placementOptionId,
          placementOption: `Placement Option ${index + 1}`,
          shipments: shipmentDetails?.length || 0,
          discount: discounts?.length || 0,
          expiry: expiration ? dayjs(expiration).format("M/D/YY") : '',
          placementFee: fees?.[0]?.value?.amount || 0,
        };
      });

      setInboundPlanDataSet({
        placementOptions
      });

      selectPlacementOption(0);
    }
  }, [inboundPlans]);

  const columnDefs = [
    {
      headerName: "Items",
      flex: 1,
      minWidth: 200,
      cellRenderer: ({ data }) => {
        const { image_url, title } = data || {};

        return (
          <div className='product-item-wrapper'>
            <img
              src={image_url || ProductImg}
              alt="Product"
            />
            <span className='product-item'>{title || '---'}</span>
          </div>
        );
      },
    },
    {
      headerName: "SKU",
      field: "msku",
      flex: 1,
      minWidth: 100
    },
    {
      headerName: "ASIN",
      field: "asin",
      flex: 1,
      minWidth: 100
    },
    {
      headerName: "Quantity",
      field: "quantity",
      flex: 1,
      minWidth: 100
    },
  ];

  const selectPlacementOption = (index) => {
    const shipments = inboundPlans[index]?.shipmentDetails || [];
    const shipmentIndex = 0;
    const shipmentId = shipments[shipmentIndex]?.shipmentId;
    const items = inboundPlans[index]?.items?.filter((item) => item.shipmentId === shipmentId) || [];

    setSelectedOptions({ placementIndex: index, shipmentIndex });
    setInboundPlanDataSet(inboundPlanDataSet => ({ ...inboundPlanDataSet, shipments, items }));
  };

  const selectShipment = (shipmentIndex) => {
    const shipmentId = inboundPlanDataSet.shipments[shipmentIndex].shipmentId;
    const items = inboundPlans[selectedOptions.placementIndex]?.items?.filter((item) => item.shipmentId === shipmentId) || {};

    setSelectedOptions(selectedOptions => ({ ...selectedOptions, shipmentIndex }));
    setInboundPlanDataSet(inboundPlanDataSet => ({ ...inboundPlanDataSet, items }));
  };

  const handleCancelInboundPlan = () => {
    dispatch(CancelInboundPlan({
      workOrderId
    }))
      .then(({ payload }) => {
        if (payload?.success) navigate(`/inventory/work-order/${workOrderId}`);
      });
  };


  const handleConfirmPlacementOption = () => {
    dispatch(ConfirmPlacementOption({
      workOrderId,
      placementOptionId: placementOptions[selectedOptions?.placementIndex]?.id
    }))
      .then(({ payload }) => {
        if (payload?.success) {
          if(currentUser?.role?.id === 5){
            navigate(`/shipping/${workOrderId}`);
          }else{
            navigate(`/confirm-placement/${workOrderId}`);
          }
        }
      });
  };

  const { placementOptions = [], shipments = [], items = [] } = inboundPlanDataSet || {};
  const loading = fetchingPlacementOptions || confirmingPlacementOption || cancelingInboundPlan || fetchingShipmentItems;
  const placementOptionsLoading = fetchingPlacementOptions || confirmingPlacementOption || cancelingInboundPlan;

  return (
    <PlacementOptionWrapper>
      <div className={`cards-overlay-wrapper ${placementOptionsLoading ? 'loading-accured' : ''}`}>
        <Spin wrapperClassName="placement-cards-loading" tip='Loading...' spinning={placementOptionsLoading}>
          <PlacementCard
            cardsData={placementOptions}
            activePlacementIndex={selectedOptions?.placementIndex}
            selectPlacementOption={selectPlacementOption}
          />
        </Spin>
      </div>
      <div className='content-area-wrapper'>
        <Spin tip='Loading...' wrapperClassName="placement-cards-loading" spinning={loading}>
          <div className='tabs-cards-wrapper'>
            {shipments?.map((item, index) => (
              <div key={item.shipmentId}
                className={`tab-item-wrapper ${selectedOptions?.shipmentIndex === index ? 'active' : ''}`}
                onClick={() => selectShipment(index)}>
                <div className='tab-item'>
                  <p>FC ID</p>
                  <p>{item?.fcId}</p>
                </div>
                <div className='tab-item'>
                  <p>SKUs Count</p>
                  <p>{inboundPlans[selectedOptions.placementIndex]?.items?.filter(({ shipmentId }) => item.shipmentId === shipmentId)?.length || 0}</p>
                </div>
                <div className='tab-item'>
                  <p>No of bundles</p>
                  <p>
                    {
                      inboundPlans[selectedOptions.placementIndex]?.items
                        ?.filter(({ shipmentId }) => item.shipmentId === shipmentId)
                        ?.reduce((sum, item) => sum + (item?.quantity || 0), 0)
                    }
                  </p>
                </div>
                <div className='tab-item'>
                  <p>Mileage</p>
                  <p>---</p>
                </div>
              </div>
            ))}
          </div>
          <AgGrid
            user_referrals={items}
            defaultColDef={defaultColDef}
            columnDefinitions={columnDefs}
            rowHeight={64}
            headerHeight={36}
            height="234px"
            masterDetail={true}
            detailRowAutoHeight={true}
            getRowNodeId={({ msku }) => (msku)}
            className="sku-table"
          />
        </Spin>
        <footer>
          <Button
            text="Cancel Plan"
            className="danger mb-0"
            minWidth="90px"
            onClick={handleCancelInboundPlan}
            disabled={loading}
          />
          <Tooltip placement='top'>
            <span>
              <Button
                text="Confirm Placement Option"
                className="mb-0"
                minWidth="90px"
                onClick={handleConfirmPlacementOption}
                disabled={loading || ['Confirmed', 'In Progress', 'Done'].includes(workOrderDetails?.status) || !placementOptions[selectedOptions?.placementIndex]?.id}
              />
            </span>
          </Tooltip>
        </footer>
      </div>
    </PlacementOptionWrapper>
  )
}

export default PlacementOption;
